<script module lang="ts">
  // RadioCardGroup is the primitive behind every grid picker in the
  // design editor — wallpaper style, button variant, radius, shadow,
  // pattern. Each caller supplies a preview snippet, and the component
  // lays them out in a responsive grid. Stories here pin the layout
  // responsiveness (2 / 3 / 4 / 5 columns) and the selected/unselected
  // visual contract.
  import { defineMeta } from '@storybook/addon-svelte-csf'
  import RadioCardGroup, { type RadioOption } from './RadioCardGroup.svelte'

  let selectedStyle = $state('solid')
  let selectedRadius = $state('rounder')
  let selectedShadow = $state('none')
  let selectedWallpaper = $state('fill')
  let selectedPattern = $state('grid')

  const variantOptions: RadioOption[] = [
    { value: 'solid', label: 'Solid', description: '가장 기본적인 꽉 찬 버튼' },
    { value: 'glass', label: 'Glass', description: '반투명 표면과 blur' },
    { value: 'outline', label: 'Outline', description: '강조색 테두리 중심' },
  ]

  const radiusOptions: RadioOption[] = [
    { value: 'square', label: 'Square' },
    { value: 'round', label: 'Round' },
    { value: 'rounder', label: 'Rounder' },
    { value: 'full', label: 'Full' },
  ]

  const shadowOptions: RadioOption[] = [
    { value: 'none', label: 'None' },
    { value: 'soft', label: 'Soft' },
    { value: 'strong', label: 'Strong' },
    { value: 'hard', label: 'Hard' },
  ]

  const wallpaperOptions: RadioOption[] = [
    { value: 'fill', label: 'Fill' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'blur', label: 'Blur' },
    { value: 'pattern', label: 'Pattern' },
    { value: 'image', label: 'Image' },
  ]

  const patternOptions: RadioOption[] = [
    { value: 'grid', label: 'Grid' },
    { value: 'morph', label: 'Morph' },
    { value: 'organic', label: 'Organic' },
    { value: 'matrix', label: 'Matrix' },
  ]

  const { Story } = defineMeta({
    component: RadioCardGroup,
    title: 'Components/ProfileEditor/RadioCardGroup',
    tags: ['autodocs'],
    parameters: { layout: 'centered' },
  })
</script>

{#snippet labelOnlyPreview(_option: RadioOption)}
  <div class="h-10 rounded-2xl bg-stone-100"></div>
{/snippet}

{#snippet radiusPreview(option: RadioOption)}
  {@const radius = ({ square: '0px', round: '8px', rounder: '16px', full: '9999px' } as Record<string, string>)[option.value] || '16px'}
  <div class="h-10 bg-stone-950" style={`border-radius:${radius};`}></div>
{/snippet}

{#snippet shadowPreview(option: RadioOption)}
  {@const shadow = ({ none: 'none', soft: '0 8px 20px rgba(15,23,42,0.12)', strong: '0 14px 28px rgba(15,23,42,0.2)', hard: '4px 4px 0 rgba(15,23,42,0.85)' } as Record<string, string>)[option.value] || 'none'}
  <div class="h-10 rounded-2xl bg-white" style={`box-shadow:${shadow};`}></div>
{/snippet}

<!--
  ─── Column counts ─────────────────────────────────────────────────
-->

<Story name="Columns · 3 (button variants)">
  {#snippet template()}
    <div style="width: 500px; padding: 32px;">
      <RadioCardGroup
        value={selectedStyle}
        options={variantOptions}
        onChange={(v) => (selectedStyle = v)}
        preview={labelOnlyPreview}
      />
    </div>
  {/snippet}
</Story>

<Story name="Columns · 4 (radius picker)">
  {#snippet template()}
    <div style="width: 560px; padding: 32px;">
      <RadioCardGroup
        value={selectedRadius}
        options={radiusOptions}
        columns={4}
        onChange={(v) => (selectedRadius = v)}
        preview={radiusPreview}
      />
    </div>
  {/snippet}
</Story>

<Story name="Columns · 4 (shadow picker)">
  {#snippet template()}
    <div style="width: 560px; padding: 32px;">
      <RadioCardGroup
        value={selectedShadow}
        options={shadowOptions}
        columns={4}
        onChange={(v) => (selectedShadow = v)}
        preview={shadowPreview}
      />
    </div>
  {/snippet}
</Story>

<Story name="Columns · 5 (wallpaper picker)">
  {#snippet template()}
    <div style="width: 700px; padding: 32px;">
      <RadioCardGroup
        value={selectedWallpaper}
        options={wallpaperOptions}
        columns={5}
        onChange={(v) => (selectedWallpaper = v)}
        preview={labelOnlyPreview}
      />
    </div>
  {/snippet}
</Story>

<Story name="Columns · 2 (pattern pairs)">
  {#snippet template()}
    <div style="width: 360px; padding: 32px;">
      <RadioCardGroup
        value={selectedPattern}
        options={patternOptions}
        columns={2}
        onChange={(v) => (selectedPattern = v)}
        preview={labelOnlyPreview}
      />
    </div>
  {/snippet}
</Story>

<!--
  ─── Edge cases ────────────────────────────────────────────────────
-->

<Story name="Edge · no preview snippet">
  {#snippet template()}
    <div style="width: 400px; padding: 32px;">
      <RadioCardGroup
        value="solid"
        options={variantOptions}
        onChange={() => {}}
      />
    </div>
  {/snippet}
</Story>

<Story name="Edge · single option (always selected)">
  {#snippet template()}
    <div style="width: 400px; padding: 32px;">
      <RadioCardGroup
        value="only"
        options={[{ value: 'only', label: '하나뿐인 옵션', description: '다른 선택지 없음' }]}
        onChange={() => {}}
        preview={labelOnlyPreview}
      />
    </div>
  {/snippet}
</Story>
